import { Directive, ElementRef, HostListener, inject, input, OnDestroy } from '@angular/core';

/**
 * Displays a lightweight floating tooltip for any element with a `tooltip` input.
 */
@Directive({
  selector: '[tooltip]',
  standalone: true,
})
export class TooltipDirective implements OnDestroy {
  /** Host element used as the positioning anchor for the tooltip. */
  private readonly el = inject(ElementRef<HTMLElement>);
  /** Tooltip DOM node currently attached to the document, if any. */
  private tip: HTMLElement | null = null;

  /** Text displayed inside the tooltip. */
  readonly tooltip         = input<string>('');
  /** Preferred side of the host element where the tooltip should appear. */
  readonly tooltipPosition = input<'top' | 'bottom' | 'left' | 'right'>('top');

  /**
   * Creates and positions the tooltip when the host is hovered.
   */
  @HostListener('mouseenter')
  show(): void {
    const text = this.tooltip();
    if (!text) return;

    const tip = document.createElement('div');
    tip.textContent = text;
    Object.assign(tip.style, {
      position:       'fixed',
      background:     'var(--talos-ink)',
      color:          'var(--talos-bg)',
      fontSize:       '11px',
      fontWeight:     '500',
      fontFamily:     'inherit',
      padding:        '4px 10px',
      borderRadius:   '6px',
      whiteSpace:     'nowrap',
      pointerEvents:  'none',
      zIndex:         '99999',
      opacity:        '0',
      transition:     'opacity 0.12s ease',
      boxShadow:      '0 2px 8px rgb(var(--talos-ink-rgb) / 0.18)',
    });
    document.body.appendChild(tip);
    this.tip = tip;

    requestAnimationFrame(() => {
      if (!this.tip) return;
      const r  = this.el.nativeElement.getBoundingClientRect();
      const tw = tip.offsetWidth;
      const th = tip.offsetHeight;
      const gap = 6;
      let left: number, top: number;

      switch (this.tooltipPosition()) {
        case 'bottom':
          left = r.left + r.width / 2 - tw / 2;
          top  = r.bottom + gap;
          break;
        case 'left':
          left = r.left - tw - gap;
          top  = r.top + r.height / 2 - th / 2;
          break;
        case 'right':
          left = r.right + gap;
          top  = r.top + r.height / 2 - th / 2;
          break;
        default: // top
          left = r.left + r.width / 2 - tw / 2;
          top  = r.top - th - gap;
      }

      tip.style.left    = `${Math.max(4, left)}px`;
      tip.style.top     = `${Math.max(4, top)}px`;
      tip.style.opacity = '1';
    });
  }

  /**
   * Removes the active tooltip when the host is left or clicked.
   */
  @HostListener('mouseleave')
  @HostListener('click')
  hide(): void {
    this.tip?.remove();
    this.tip = null;
  }

  /**
   * Cleans up the tooltip element if Angular destroys the host while it is open.
   */
  ngOnDestroy(): void {
    this.tip?.remove();
  }
}
